import React, { useEffect, useState } from "react";

import Dialog from "@material-ui/core/Dialog";
import Avatar from "@material-ui/core/Avatar";
import Checkbox from "@material-ui/core/Checkbox";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import SearchIcon from "@material-ui/icons/Search";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { formatarTelefone } from "../../utils/phone";

// Encaminhar uma mensagem (texto, foto, audio, arquivo) para o proprio
// cliente ou para outros clientes do atendente. Vendedor ve so os dele;
// admin/operador veem os atendimentos abertos/pendentes de todos.

const MAX = 10;

const useStyles = makeStyles((theme) => {
  const escuro = theme.palette.type === "dark";
  return {
    papel: { borderRadius: 14, width: 420, maxWidth: "calc(100vw - 32px)" },
    topo: { padding: "16px 18px 8px" },
    titulo: { fontWeight: 700, fontSize: "1rem", color: theme.palette.text.primary },
    previa: {
      marginTop: 8,
      padding: "6px 10px",
      borderRadius: 8,
      fontSize: "0.78rem",
      color: theme.palette.text.secondary,
      backgroundColor: escuro ? "rgba(255,255,255,0.05)" : "#f5f7f6",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    busca: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      margin: "10px 18px 6px",
      padding: "6px 10px",
      borderRadius: 10,
      border: `1px solid ${escuro ? "rgba(255,255,255,0.1)" : "#e3e8e5"}`,
      "& svg": { fontSize: 18, color: theme.palette.text.secondary },
    },
    lista: { maxHeight: 340, overflowY: "auto", padding: "0 8px", ...theme.scrollbarStyles },
    item: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "6px 10px",
      borderRadius: 10,
      cursor: "pointer",
      "&:hover": { backgroundColor: escuro ? "rgba(255,255,255,0.05)" : "#f3f6f4" },
    },
    nome: { fontSize: "0.86rem", fontWeight: 600, color: theme.palette.text.primary, lineHeight: 1.2 },
    numero: { fontSize: "0.74rem", color: theme.palette.text.secondary },
    selo: {
      marginLeft: 6,
      fontSize: "0.6rem",
      fontWeight: 700,
      padding: "0 6px",
      borderRadius: 7,
      lineHeight: "15px",
      color: "#137a43",
      backgroundColor: "rgba(30,152,85,0.14)",
    },
    vazio: { padding: "18px 0", textAlign: "center", fontSize: "0.8rem", color: theme.palette.text.secondary },
    rodape: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 18px 14px",
      borderTop: `1px solid ${theme.palette.divider}`,
      marginTop: 6,
    },
    contagem: { fontSize: "0.76rem", color: theme.palette.text.secondary },
    botoes: { display: "flex", gap: 8 },
    botao: {
      border: "none",
      cursor: "pointer",
      padding: "7px 14px",
      borderRadius: 8,
      fontSize: "0.82rem",
      fontWeight: 700,
      fontFamily: "inherit",
      "&:disabled": { opacity: 0.6, cursor: "default" },
    },
    cancelar: { background: "transparent", color: theme.palette.text.secondary },
    enviar: { background: "#1E9855", color: "#fff" },
  };
});

const descreverMensagem = (m) => {
  if (!m) return "";
  const tipos = { image: "📷 Foto", video: "🎥 Vídeo", audio: "🎤 Áudio", ptt: "🎤 Áudio", document: "📄 Arquivo", sticker: "Figurinha" };
  const corpo = String(m.body || "").trim();
  if (m.mediaType && m.mediaType !== "chat") {
    const legenda = corpo && !/\.\w{2,5}$/.test(corpo) ? ` — ${corpo}` : "";
    return `${tipos[m.mediaType] || "Mídia"}${legenda}`;
  }
  return corpo;
};

const EncaminharModal = ({ open, onClose, message }) => {
  const classes = useStyles();
  const [busca, setBusca] = useState("");
  const [destinos, setDestinos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [marcados, setMarcados] = useState([]);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let ativo = true;
    setCarregando(true);
    const t = setTimeout(() => {
      api
        .get("/encaminhar/destinos", { params: { q: busca, ticketId: message?.ticketId } })
        .then(({ data }) => ativo && setDestinos(Array.isArray(data) ? data : []))
        .catch(() => ativo && setDestinos([]))
        .finally(() => ativo && setCarregando(false));
    }, 250);
    return () => {
      ativo = false;
      clearTimeout(t);
    };
  }, [open, busca, message]);

  useEffect(() => {
    if (open) {
      setBusca("");
      setMarcados([]);
    }
  }, [open]);

  const alternar = (id) =>
    setMarcados((m) => (m.includes(id) ? m.filter((x) => x !== id) : m.length >= MAX ? m : [...m, id]));

  const encaminhar = async () => {
    if (!marcados.length || enviando) return;
    setEnviando(true);
    try {
      const { data } = await api.post(`/messages/${message.id}/encaminhar`, { ticketIds: marcados });
      if (data?.enviados) toast.success(`Mensagem encaminhada para ${data.enviados} conversa(s).`);
      if (data?.falhas?.length) toast.warn(`${data.falhas.length} não foi(ram) enviada(s): ${data.falhas.map((f) => f.motivo).join(", ")}`);
      onClose();
    } catch (err) {
      toastError(err);
    }
    setEnviando(false);
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ className: classes.papel }}>
      <div className={classes.topo}>
        <div className={classes.titulo}>Encaminhar mensagem</div>
        <div className={classes.previa} title={descreverMensagem(message)}>
          {descreverMensagem(message)}
        </div>
      </div>
      <div className={classes.busca}>
        <SearchIcon />
        <InputBase
          fullWidth
          autoFocus
          placeholder="Buscar cliente por nome ou número"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          style={{ fontSize: "0.85rem" }}
        />
        {carregando && <CircularProgress size={14} />}
      </div>
      <div className={classes.lista}>
        {!carregando && !destinos.length && <div className={classes.vazio}>Nenhum cliente encontrado.</div>}
        {destinos.map((d) => (
          <div key={d.ticketId} className={classes.item} onClick={() => alternar(d.ticketId)}>
            <Checkbox
              size="small"
              color="primary"
              checked={marcados.includes(d.ticketId)}
              style={{ padding: 2 }}
              onClick={(e) => e.stopPropagation()}
              onChange={() => alternar(d.ticketId)}
            />
            <Avatar src={d.foto || undefined} style={{ width: 34, height: 34 }} />
            <div style={{ minWidth: 0 }}>
              <div className={classes.nome}>
                {d.nome}
                {d.atual && <span className={classes.selo}>Este cliente</span>}
              </div>
              <div className={classes.numero}>{formatarTelefone(d.numero)}</div>
            </div>
          </div>
        ))}
      </div>
      <div className={classes.rodape}>
        <span className={classes.contagem}>
          {marcados.length ? `${marcados.length} selecionado(s)` : `Até ${MAX} destinos`}
        </span>
        <div className={classes.botoes}>
          <button type="button" className={`${classes.botao} ${classes.cancelar}`} onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className={`${classes.botao} ${classes.enviar}`}
            disabled={!marcados.length || enviando}
            onClick={encaminhar}
          >
            {enviando ? "Encaminhando..." : "Encaminhar"}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default EncaminharModal;
